import { computed, ref, watch, type Ref } from 'vue';
import { useRouter } from 'vue-router';
import { isAxiosError } from 'axios';
import { listMatrixCodeRevisions, type MatrixCodeRevisionsPage } from '@/entities/Matrix';
import { setBreadcrumbTrail, useCustomToast, type Breadcrumb } from '@/shared/lib';
import { MATRIX_NOT_FOUND_TOAST_TITLE } from '../config';
import type { MatrixRevisionScope } from './useModuleMatrixRevisionScope';

export const QUALITY_RECORDS_BREADCRUMB: Breadcrumb = {
  name: 'Registros da Qualidade',
  path: '/',
  clickable: true,
};

const FIRST_PAGE = { page: 1, itemsPerPage: 10 };

export function useQualityRecordMatrixPage(
  prefix: Ref<string>,
  scope: MatrixRevisionScope | null = null
) {
  const router = useRouter();
  const { showErrorToast } = useCustomToast();

  const revisionsPage = ref<MatrixCodeRevisionsPage | null>(null);
  const loading = ref(true);
  const error = ref(false);
  let latestRequest = 0;

  const title = computed(() => scope?.title ?? revisionsPage.value?.head.code ?? '');

  const loadingTrail = (): Breadcrumb[] => scope?.breadcrumbTrail ?? [QUALITY_RECORDS_BREADCRUMB];
  const loadedTrail = (): Breadcrumb[] =>
    scope?.breadcrumbTrail ?? [
      QUALITY_RECORDS_BREADCRUMB,
      { name: title.value, path: '', clickable: false },
    ];

  async function load(): Promise<void> {
    // Only the newest request may publish: a late 404 of the previous prefix would send the right
    // page back to Home.
    const request = ++latestRequest;

    loading.value = true;
    error.value = false;
    revisionsPage.value = null;
    setBreadcrumbTrail(loadingTrail());

    try {
      const page = await listMatrixCodeRevisions(
        prefix.value,
        scope ? { ...FIRST_PAGE, matrixId: scope.matrixId } : FIRST_PAGE
      );
      if (request !== latestRequest) return;
      revisionsPage.value = page;
      setBreadcrumbTrail(loadedTrail());
    } catch (err) {
      if (request !== latestRequest) return;
      if (isAxiosError(err) && err.response?.status === 404) {
        showErrorToast({ title: MATRIX_NOT_FOUND_TOAST_TITLE, type: 'error' });
        await router.replace({ name: 'Home' });
        return;
      }
      error.value = true;
    } finally {
      if (request === latestRequest) {
        loading.value = false;
      }
    }
  }

  watch(prefix, load, { immediate: true });

  return {
    revisionsPage,
    title,
    loading,
    error,
    load,
  };
}
